import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { NbToastrService } from '@nebular/theme';

declare let newrelic;

// Known noise that surfaces in the global handler or New Relic's auto-capture
// but requires no user action and shouldn't be paged. Add an entry when a
// third-party SDK or browser quirk produces benign errors at scale. Consumed
// by both GlobalErrorHandler (Angular zone errors) and main.ts's
// newrelic.setErrorHandler (window.onerror / unhandled-rejection captures).
interface SilencedErrorPattern {
  name: string;
  match: (e: { name?: string; message?: string; stack?: string }) => boolean;
}

const SILENCED_ERROR_PATTERNS: ReadonlyArray<SilencedErrorPattern> = [
  {
    name: 'firebase-installations-not-registered',
    // Stale installation IDs in IndexedDB after a long absence or App Check
    // rejection. The SDK clears the cached ID and re-registers transparently.
    match: (e) => (e?.message || '').indexOf('installations/not-registered') >= 0
  },
  {
    name: 'firestore-offline',
    // User lost network mid-read. Firestore reconnects when they're back.
    match: (e) => (e?.message || '').indexOf('client is offline') >= 0
  },
  {
    name: 'recaptcha',
    // App Check's ReCaptchaV3Provider — minified TypeErrors from gstatic. App
    // Check auto-refreshes, so these don't affect the session.
    match: (e) => {
      const stack = e?.stack || '';
      return stack.indexOf('recaptcha__en.js') >= 0 || stack.indexOf('gstatic.com/recaptcha') >= 0;
    }
  },
  {
    name: 'nb-overlay-no-layout',
    // Nebular overlay (toast/dialog) firing before <nb-layout> has rendered —
    // typically during early bootstrap or routes outside the shell. Nothing
    // actionable; we also guard the toast call below to avoid feedback loops.
    match: (e) => (e?.message || '').indexOf('NbOverlayContainerAdapter') >= 0
  },
  {
    name: 'third-party-script-blocked',
    // Ad/analytics/tag scripts blocked by ad blockers, privacy extensions, or
    // network filters. We don't control these and the app degrades gracefully.
    // Match both the generic NR phrasing and known offending hosts.
    match: (e) => {
      const msg = e?.message || '';
      if (msg.indexOf('remote script failed') >= 0) return true;
      const haystack = msg + ' ' + (e?.stack || '');
      return (
        haystack.indexOf('adsrvr.org') >= 0 ||
        haystack.indexOf('googletagmanager.com') >= 0 ||
        haystack.indexOf('connect.facebook.net') >= 0 ||
        haystack.indexOf('googlesyndication.com') >= 0 ||
        haystack.indexOf('doubleclick.net') >= 0
      );
    }
  },
  {
    name: 'web-vitals-report',
    // GTM/GA4 inject the web-vitals library as an eval'd script, and its observers
    // throw "Cannot read properties of undefined (reading 'startTime')" when an
    // entry list comes back empty — commonly on bfcache restores and prerendered
    // navigations. The stack is all <anonymous>/VM### with no URL, so the
    // third-party filter above cannot match it by host; key off the library's own
    // symbol instead. Not our code: web-vitals is not a dependency of this app.
    match: (e) => {
      const haystack = (e?.message || '') + ' ' + (e?.stack || '');
      return haystack.indexOf('reportAllChanges') >= 0;
    }
  },
  {
    name: 'cross-origin-script-error',
    // Browsers strip cross-origin script errors of all detail and surface them
    // as "Script error." (or an empty message). With no stack and no message
    // there's nothing actionable; we can't even tell which script it was.
    match: (e) => {
      const msg = e?.message || '';
      return msg === '' || msg === 'Script error.' || msg.indexOf('Script error.') >= 0;
    }
  },
  {
    name: 'idb-connection-closing',
    // Firebase persistence races against page unload — the SDK tries a final
    // IndexedDB transaction while the tab is closing. No user impact.
    match: (e) => (e?.message || '').indexOf('database connection is closing') >= 0
  }
];

export function shouldSilenceError(error: unknown): SilencedErrorPattern | undefined {
  if (!error) return undefined;
  return SILENCED_ERROR_PATTERNS.find((p) => p.match(error as Error));
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(private injector: Injector) {}
  handleError(error: Error): void {
    const silenced = shouldSilenceError(error);
    const fe = error as { code?: string; firestoreOp?: string; firestorePath?: string };
    // Firestore permission-denied is a transient App Check / token race that
    // self-heals — still report it, just don't toast the user.
    const isPermissionDenied = fe?.code === 'permission-denied';

    // Known noise: log it locally for debugging, but never notify New Relic and
    // never toast the user. This previously also required a Firestore token race,
    // so every other entry in SILENCED_ERROR_PATTERNS still paged New Relic and
    // still showed a red "We hit a glitch" — despite those entries existing to
    // prevent exactly that. The window.onerror path already suppresses the whole
    // list via newrelic.setErrorHandler in main.ts; this makes the Angular path
    // agree with it.
    if (silenced) {
      console.error(`Global Error Handler (${silenced.name}, suppressed)`, error);
      return;
    }

    newrelic.noticeError(error, {
      globalHandler: true,
      handled: isPermissionDenied,
      // firestore* set only for errors tagged by DbService; FACET on them in NR.
      firestoreCode: fe?.code,
      firestoreOp: fe?.firestoreOp,
      firestorePath: fe?.firestorePath
    });

    if (!isPermissionDenied) {
      // Toast can throw if <nb-layout> isn't mounted yet (early bootstrap, SSR
      // hydration, off-shell routes). Swallow that — we still report below.
      try {
        this.injector.get(NbToastrService).danger(error.message, 'We hit a glitch', {
          duration: 10000,
          destroyByClick: true
        });
      } catch {
        // intentionally empty — avoid recursion through the handler
      }
    }
  }
}
